/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEventHandler,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import ReactPlayer from 'react-player';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {
  EventCodeValueType,
  getEventName
} from '@ion/core/src/services/EventCodes';
import {GenericObject} from '@ion/core/src/types/entities';
import {ErrorState} from '../../../notifications/ErrorState';
import {dlsColor} from '../../../theme/theme';
import useThemeContext from '../../../theme/useThemeContext';
import {getFormattedDateTimeZoneParts} from '../../../utils/ComponentUtils';
import useVideoPlayer from '../../videoplayer/hooks/useVideoPlayer';
import {VisionPlayerControls} from './VisionPlayerControls';

export interface VisionPlayerProps {
  event: GenericObject;
  videoSource: string;
  eventClick?: MouseEventHandler<HTMLSpanElement>;
  audio?: boolean;
  playbackLabel?: string;
}

const useStyles = makeStyles(() => ({
  card : {
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    alignItems      : 'flex-start',
    height          : '286px',
    padding         : '24px 16px',
    gap             : '8px',
    backgroundColor : dlsColor('white'),
    borderBottom    : `1px solid ${dlsColor('grey7')}`
  },
  player : {
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'flex-start',
    width         : '100%'
  },
  preview : {
    cursor          : 'pointer',
    width           : '100%',
    height          : '136px',
    backgroundColor : dlsColor('grey5'),
    borderRadius    : '12px 12px 0px 0px',
    overflow        : 'hidden'
  },
  // a video wider than the preview (two cameras side by side) is stretched
  // to the preview's height, rather than letterboxed above and below ...
  stretchedPreview : {
    '& video' : {
      objectFit : 'fill'
    }
  },
  title : {
    fontFamily    : 'Roboto',
    fontWeight    : 500,
    fontSize      : '13px',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    color         : '#276AD8',
    cursor        : 'pointer'
  },
  timestamp : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '12px',
    lineHeight    : '16px',
    letterSpacing : '0px',
    whiteSpace    : 'nowrap',
    verticalAlign : 'middle',
    color         : dlsColor('grey2')
  }
}));

/**
 * VisionPlayer renders the current theme's Vision player card - a compact
 * video preview, a restyled play/volume/fullscreen control bar
 * (`VisionPlayerControls`), and the featured event's title/timestamp below
 * it. It is built on the same `react-player` engine and shared
 * `useVideoPlayer` state hook that
 * `@ion/dls/src/components/videoplayer/VideoPlayer` and `MiniVideoPlayer`
 * already use - the identical pattern `MiniVideoPlayer` follows for its own
 * custom-styled control row - so no playback logic is reimplemented here,
 * only the surrounding markup and styling are new.
 *
 * ```typescript
 *
 *   import {VisionPlayer} from
 *     '@ion/dls/src/components/vision/current/VisionPlayer';
 *   ...
 *
 *   <VisionPlayer event={featuredEvent}
 *     videoSource={videoSource}
 *     eventClick={function() { onIonVisionClick(featuredEvent); }}
 *     audio />
 *
 * ```
 *
 */
export const VisionPlayer = ({
  event,
  videoSource,
  eventClick,
  audio = false,
  playbackLabel
}: VisionPlayerProps) => {
  const classes                     = useStyles();
  const {isCurrent: isCurrentTheme} = useThemeContext();
  const [isWideVideo, setWideVideo] = useState(false);
  const previewRef                  = useRef<HTMLDivElement>(null);

  const {date, time} = useMemo(() => (
    getFormattedDateTimeZoneParts(event?.appmsgEventTime)
  ), [event?.appmsgEventTime]);

  const {
    handleVolumeChange,
    handleVolumeIcon,
    handleVideoHover,
    handleVideoLeave,
    handlePlayPause,
    handleProgress,
    handleDuration,
    handleIsReady,
    handleEnded,
    handlePause,
    isHovering,
    setFailed,
    duration,
    restart,
    playing,
    failed,
    volume,
    retry
  } = useVideoPlayer();

  // the preview plays while hovered, as the legacy player's does ...
  const isPlaying = playing || isHovering;

  const label = isCurrentTheme
    ? (playbackLabel ?? translate(`common:vision.${isPlaying ? 'pause' : 'play'}`))
    : undefined;

  // compares the loaded video's shape to the preview's - a wider video would
  // otherwise leave a gap above and below it ...
  const onReady = useCallback(function(player) {
    const video   = player?.getInternalPlayer?.();
    const preview = previewRef.current;
    if (video?.videoWidth && video?.videoHeight && preview?.clientWidth && preview?.clientHeight) {
      setWideVideo(video.videoWidth / video.videoHeight > preview.clientWidth / preview.clientHeight);
    }
    handleIsReady();
  }, [handleIsReady]);

  const onError = useCallback(function() {
    setFailed(true);
  }, [setFailed]);

  // the video and its expand control open the event in the Vision dialog -
  // rather than the browser's own fullscreen, whose exit (e.g. by Esc) left
  // the control showing as expanded ...
  const onOpenEvent = useCallback(function(clickEvent) {
    eventClick?.(clickEvent);
  }, [eventClick]);

  return (
    <div className={classes.card}>
      <div className={classes.player}>
        {failed ? (
          <ErrorState onRefresh={retry} show />
        ) : (
          <>
            <div ref={previewRef}
              className={classnames(classes.preview, isWideVideo && classes.stretchedPreview)}
              onMouseEnter={handleVideoHover}
              onMouseLeave={handleVideoLeave}
              onClick={onOpenEvent}>
              <ReactPlayer url={videoSource}
                playing={isPlaying}
                duration={duration}
                volume={audio ? volume : 0}
                width='100%'
                height='100%'
                onProgress={handleProgress}
                onDuration={handleDuration}
                onReady={onReady}
                onPause={handlePause}
                onEnded={handleEnded}
                onError={onError} />
            </div>
            <VisionPlayerControls playing={isPlaying}
              restart={restart}
              volume={volume}
              audio={audio}
              fullScreen={false}
              label={label}
              onPlayPause={handlePlayPause}
              onVolumeIcon={handleVolumeIcon}
              onVolumeChange={handleVolumeChange}
              onClickFullscreen={onOpenEvent} />
          </>
        )}
      </div>
      <span className={classes.title} onClick={eventClick}>
        {getEventName({
          from : EventCodeValueType.eventCode,
          val  : parseInt(event?.avlEventCode)
        })}
      </span>
      <span className={classes.timestamp}>
        {`${date} ${translate('common:time.at', {time})}`}
      </span>
    </div>
  );
};

export default React.memo(VisionPlayer);
